import { inject, Injectable } from '@angular/core';
import {
  Auth,
  confirmPasswordReset,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { TenantAuthClaims, TenantConfig } from '@models';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  OperatorFunction,
  Subject,
} from 'rxjs';
import { TenantContextService } from './tenant-context.service';
import { SportContextService } from './sport-context.service';
import { APP_TENANT_ID } from './app-tenant-id.token';

export interface AuthStatus {
  /** Undefined while Firebase is resolving the auth state. */
  loggedIn: boolean | undefined;
  uid?: string;
}

const EMPTY_LOGGED: AuthStatus = { loggedIn: undefined };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authStatus = new BehaviorSubject<AuthStatus>(EMPTY_LOGGED);

  /** Emits once when the user logs out. Used to complete long-lived observables. */
  public onLogout = new Subject<void>();

  /**
   * Emits true when the authenticated user is not a member of the tenant
   * this app was built for. Only relevant in mobile apps (APP_TENANT_ID provided).
   * When true, the app should show an error screen instead of the dashboard.
   */
  public notAMemberOfThisApp$ = new BehaviorSubject<boolean>(false);

  public isLoggedIn$: Observable<boolean> = this.authStatus.pipe(
    filter((status) => status.loggedIn !== undefined),
    map((v) => Boolean(v.loggedIn)),
  );

  /** Emits only after auth is resolved and user is logged in. */
  public uid$: Observable<string> = this.authStatus.pipe(
    filter(
      (status): status is Required<AuthStatus> =>
        status.loggedIn === true && status.uid !== undefined,
    ) as OperatorFunction<AuthStatus, Required<AuthStatus>>,
    map((v) => v.uid),
  );

  /** tenantId this binary was built for. Null in admin panel. */
  private readonly appTenantId =
    inject(APP_TENANT_ID, { optional: true }) ?? null;

  constructor(
    private auth: Auth,
    private functions: Functions,
    private firestore: Firestore,
    private tenantContext: TenantContextService,
    private sportContext: SportContextService,
  ) {
    const appTenantId = this.appTenantId;

    onAuthStateChanged(this.auth, async (user) => {
      if (user?.email) {
        let claims: TenantAuthClaims;
        try {
          // forceRefresh: true ensures we always get the latest custom claims
          // (important after setting superAdmin or tenantId via Admin SDK)
          const tokenResult = await user.getIdTokenResult(true);
          claims = tokenResult.claims as unknown as TenantAuthClaims;
        } catch {
          // Cached token failed — try the cached token without forcing refresh.
          try {
            const tokenResult = await user.getIdTokenResult(false);
            claims = tokenResult.claims as unknown as TenantAuthClaims;
          } catch {
            // Both failed — sign out to clear Firebase IndexedDB state so the
            // loginGuard doesn't see a stale user and cause an infinite redirect
            // loop (blank screen that required clearing app data manually).
            this.tenantContext.clear();
            this.sportContext.clear();
            try {
              await signOut(this.auth);
            } catch {
              /* ignore */
            }
            this.authStatus.next({ loggedIn: false });
            return;
          }
        }

        // Cross-tenant guard (mobile app only): the user authenticated correctly
        // but is NOT a member of the tenant this binary was built for. We must
        // NOT keep this broken session around — on refresh it would cascade into
        // `tenantId not yet resolved` crashes. We sign out cleanly BEFORE any
        // stream touches `collections`.
        //
        // NOTE: strict membership only (`claims.tenants?.[appTenantId]`). A
        // superAdmin is NOT exempt here: in a tenant mobile app, a superadmin
        // who has no role in `appTenantId` is not a member of THIS app and must
        // be blocked like any other non-member.
        if (appTenantId && !claims.tenants?.[appTenantId]) {
          this.notAMemberOfThisApp$.next(true);
          this.tenantContext.clear();
          this.sportContext.clear();
          try {
            await signOut(this.auth);
          } catch {
            /* ignore */
          }
          this.authStatus.next({ loggedIn: false });
          return;
        }

        this.notAMemberOfThisApp$.next(false);
        this.tenantContext.setFromClaims(claims);
        await this.initSportContext(
          this.tenantContext.tenantIdOrNull ?? undefined,
        );
        this.authStatus.next({ loggedIn: true, uid: user.uid });
      } else {
        this.tenantContext.clear();
        this.sportContext.clear();
        this.notAMemberOfThisApp$.next(false);
        this.authStatus.next({ loggedIn: false });
      }
    });
  }

  private async initSportContext(tenantId?: string): Promise<void> {
    if (!tenantId) return;
    try {
      const snap = await getDoc(doc(this.firestore, 'tenants', tenantId));
      if (snap.exists()) {
        this.sportContext.initFromTenantConfig(snap.data() as TenantConfig);
      }
    } catch {
      // Non-critical — sport context will remain empty
    }
  }

  login(email: string, password: string) {
    this.authStatus.next(EMPTY_LOGGED);
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  /**
   * Federate registration flow: sign in with the credentials of an account that
   * was just created by the `createUserAndAuth` Cloud Function and FORCE a fresh
   * ID token so the membership claims (`tenants: { [tenantId]: PLAYER }`) set by
   * that function are present in the token BEFORE `payLicense` is called and
   * BEFORE the cross-tenant guard (`onAuthStateChanged`) evaluates membership.
   *
   * Without the forced refresh the very first token after sign-in could be stale
   * and (a) make `payLicense` fail to resolve the tenantId (401/precondition) or
   * (b) make the cross-tenant guard expel the brand-new member. The guard itself
   * already calls `getIdTokenResult(true)`, but we refresh here too so the claims
   * are guaranteed in `this.auth.currentUser`'s token regardless of listener
   * timing.
   */
  async loginAndRefreshClaims(email: string, password: string): Promise<void> {
    const credential = await this.login(email, password);
    // Force-refresh the ID token to pull the claims set server-side just now.
    await credential.user.getIdToken(true);
  }

  /**
   * Mobile app only: resolves whether the currently authenticated Firebase user
   * has a real role in the tenant this binary was built for (`APP_TENANT_ID`).
   *
   * Reads the claims directly from the Firebase user token so it is reliable
   * right after `login()` resolves, without depending on the timing of the
   * `onAuthStateChanged` listener. In the admin panel (no `APP_TENANT_ID`) it
   * always returns `true`.
   *
   * STRICT membership only: a superAdmin is NOT exempt. In a tenant mobile app a
   * superadmin who holds no role in `appTenantId` is not a member of THIS app
   * and must be blocked like any other non-member.
   */
  async isMemberOfAppTenant(): Promise<boolean> {
    if (!this.appTenantId) return true;
    const user = this.auth.currentUser;
    if (!user) return false;
    try {
      const tokenResult = await user.getIdTokenResult();
      const claims = tokenResult.claims as unknown as TenantAuthClaims;
      return !!claims.tenants?.[this.appTenantId];
    } catch {
      return false;
    }
  }

  logout() {
    this.onLogout.next();
    this.tenantContext.clear();
    this.sportContext.clear();
    return signOut(this.auth).then(() =>
      this.authStatus.next({ loggedIn: false }),
    );
  }

  /**
   * Triggers the `sendPasswordResetEmail` callable.
   *
   * The `tenantId` decides which credentials the Cloud Function uses to send
   * the email, so resolving it matters: with a tenant the CF sends through the
   * tenant's own Brevo account and logs to `tenants/{tenantId}/emails`; without
   * it, it falls back to the platform sender.
   *
   * Precedence:
   * 1. The explicit `tenantId` from the caller (admin panel, resolved from the
   *    hostname) always wins.
   * 2. Otherwise `APP_TENANT_ID`, the tenant this binary was built for. Mobile
   *    apps have no usable hostname (Capacitor serves from `localhost`), so
   *    this is the only way they can reach their tenant's sender.
   * 3. If neither is available (admin panel on an unmapped host,
   *    member-portal), we send `undefined` and the CF uses the platform route.
   */
  resetPassword(email: string, tenantId?: string | null): Promise<void> {
    const callable = httpsCallable(this.functions, 'sendPasswordResetEmail');
    return callable({
      email,
      tenantId: tenantId ?? this.appTenantId ?? undefined,
    }).then(() => undefined);
  }

  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return confirmPasswordReset(this.auth, oobCode, newPassword);
  }

  updatePassword(userId: string, newPassword: string) {
    const callable = httpsCallable(this.functions, 'resetUserPassword');
    return callable({ userId, newPassword });
  }
}
